import React from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import BrandMark from "../BrandMark";
import Seo from "../Seo";
import { useAuth } from "../../context/AuthContext";

const STEPS = [
  { id: "basics", label: "Basics", path: "/instructor/onboarding/basics" },
  { id: "expertise", label: "Expertise", path: "/instructor/onboarding/expertise" },
  { id: "credentials", label: "Credentials", path: "/instructor/onboarding/credentials" },
  { id: "media", label: "Photo", path: "/instructor/onboarding/media" },
  { id: "offerings", label: "Offerings", path: "/instructor/onboarding/offerings" },
  { id: "review", label: "Review", path: "/instructor/onboarding/review" },
];

export function statusBadgeClass(status) {
  switch (status) {
    case "Approved":
      return "bg-emerald-50 text-emerald-700";
    case "Submitted":
    case "UnderReview":
      return "bg-amber-50 text-amber-800";
    case "Rejected":
    case "Suspended":
      return "bg-red-50 text-red-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function StepMarker({ index, state }) {
  if (state === "done") {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white shadow-sm">
        <Check size={14} strokeWidth={2.5} />
      </span>
    );
  }
  if (state === "current") {
    return (
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-xs font-semibold text-white shadow-sm ring-4 ring-emerald-100">
        {index + 1}
      </span>
    );
  }
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-white text-xs font-medium text-emerald-700/70">
      {index + 1}
    </span>
  );
}

export default function InstructorShell({ mode = "dashboard" }) {
  const { user, instructor, signOut, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const profile = instructor?.profile;
  const completeness = instructor?.completeness;
  const isOnboarding = location.pathname.includes("/onboarding");

  const stepIndex = STEPS.findIndex((s) => location.pathname.startsWith(s.path));
  const progressPct =
    completeness?.score ?? Math.max(0, Math.round(((stepIndex + 1) / STEPS.length) * 100));

  return (
    <div
      className={
        isOnboarding
          ? "min-h-screen bg-gradient-to-b from-emerald-50 via-white to-slate-50"
          : "min-h-screen bg-slate-50"
      }
      style={
        isOnboarding
          ? {
              backgroundImage:
                "radial-gradient(ellipse 90% 50% at 10% -10%, rgba(16,185,129,0.14), transparent 55%), radial-gradient(ellipse 70% 40% at 100% 0%, rgba(16,185,129,0.08), transparent 50%)",
            }
          : undefined
      }
    >
      <Seo
        title="Instructor Dashboard"
        description="Manage your Yoga Retreats Booking instructor profile."
        canonicalPath="/instructor"
        robots="noindex,nofollow"
      />
      <header className="border-b border-emerald-100/80 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-3">
          <Link to="/instructor" className="flex items-center gap-2">
            <BrandMark />
            <span className="text-sm font-semibold text-gray-900 sm:text-base">
              Instructor {isOnboarding ? "onboarding" : "dashboard"}
            </span>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            {profile?.status && (
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClass(profile.status)}`}
              >
                {profile.status}
              </span>
            )}
            {isAdmin && (
              <>
                <Link to="/admin/applications" className="text-emerald-700 hover:underline">
                  Admin
                </Link>
                <Link to="/admin/partner-applications" className="text-emerald-700 hover:underline">
                  Partners
                </Link>
              </>
            )}
            <span className="hidden text-gray-500 sm:inline">{user?.email}</span>
            <button
              type="button"
              onClick={async () => {
                await signOut();
                navigate("/");
              }}
              className="text-gray-600 hover:text-gray-900"
            >
              Sign out
            </button>
          </div>
        </div>
        {isOnboarding && (
          <div className="mx-auto max-w-5xl px-4 pb-4">
            <div className="mb-2 flex items-center justify-between text-xs">
              <span className="font-medium text-emerald-900">
                {stepIndex >= 0
                  ? `Step ${stepIndex + 1} of ${STEPS.length} · ${STEPS[stepIndex].label}`
                  : "Onboarding"}
              </span>
              <span className="text-emerald-700/80">{progressPct}% complete</span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-emerald-100">
              <motion.div
                className="h-full rounded-full bg-emerald-500"
                initial={false}
                animate={{ width: `${progressPct}%` }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
          </div>
        )}
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6 sm:py-8">
        {isOnboarding ? (
          <div className="flex flex-col items-start gap-6 md:flex-row">
            <aside className="w-full shrink-0 md:w-56">
              <nav
                aria-label="Onboarding steps"
                className="rounded-2xl border border-emerald-100 bg-white/90 p-3 shadow-sm"
              >
                <p className="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-emerald-800/70">
                  Your journey
                </p>
                {/* Mobile chips */}
                <ol className="flex gap-2 overflow-x-auto pb-1 md:hidden">
                  {STEPS.map((step, index) => {
                    const isCurrent = location.pathname.startsWith(step.path);
                    const state =
                      stepIndex > index ? "done" : isCurrent ? "current" : "upcoming";
                    return (
                      <li key={step.id} className="shrink-0">
                        <NavLink
                          to={step.path}
                          className={[
                            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-medium",
                            state === "current"
                              ? "bg-emerald-600 text-white"
                              : state === "done"
                                ? "bg-emerald-50 text-emerald-800"
                                : "bg-slate-50 text-gray-500",
                          ].join(" ")}
                        >
                          <StepMarker index={index} state={state} />
                          {step.label}
                        </NavLink>
                      </li>
                    );
                  })}
                </ol>
                {/* Desktop list */}
                <ol className="hidden space-y-1 md:block">
                  {STEPS.map((step, index) => {
                    const isCurrent = location.pathname.startsWith(step.path);
                    const state =
                      stepIndex > index ? "done" : isCurrent ? "current" : "upcoming";
                    return (
                      <li key={step.id}>
                        <NavLink
                          to={step.path}
                          className={[
                            "flex items-center gap-3 rounded-xl px-2 py-2 text-sm transition-colors",
                            isCurrent
                              ? "bg-emerald-50 font-medium text-emerald-950"
                              : "text-gray-600 hover:bg-emerald-50/60 hover:text-gray-900",
                          ].join(" ")}
                        >
                          <StepMarker index={index} state={state} />
                          <span>{step.label}</span>
                        </NavLink>
                      </li>
                    );
                  })}
                </ol>
              </nav>
            </aside>
            <motion.div
              key={location.pathname}
              className="min-w-0 w-full flex-1"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              <Outlet context={{ mode }} />
            </motion.div>
          </div>
        ) : (
          <Outlet context={{ mode }} />
        )}
      </main>
    </div>
  );
}

export { STEPS };
